import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Copy, ExternalLink, Globe, RefreshCw } from 'lucide-react';
import { useAuth } from '../auth';
import { formatDateTime } from '../api';
import { EmptyState, ErrorState, PageHeader, PageSkeleton, StatusBadge } from '../components/Ui';
import {
  addDomain, checkDomain, listDomains, listPages, removeDomain, setDomainRoot,
  type DnsRecord, type DomainsResponse, type LandingPage, type PageDomain,
} from '../landing/api';
import { isApex, isValidDomain, normalizeDomain } from '../landing/model/domains';
import { FeedbackProvider, useConfirm, useToast } from '../landing/ui/feedback';

// Pages, Domains tab: the agency's own web addresses for landing pages. One
// connected domain serves every published page by its link
// (https://<domain>/<page link>) and one chosen page at its root (the
// homepage). Administrators add, check and remove domains and pick the
// homepage; everyone else sees the list. While a domain is not live yet, its
// DNS records show under it with Copy buttons.

const STATUS: Record<PageDomain['status'], { label: string; tone: 'success' | 'warning' | 'danger' }> = {
  active: { label: 'Live', tone: 'success' },
  pending: { label: 'Waiting for DNS', tone: 'warning' },
  error: { label: 'Error', tone: 'danger' },
};

/** The homepage choices: published pages first, then drafts, each by name. */
export function homepageOptions(pages: Pick<LandingPage, 'id' | 'name' | 'status'>[]) {
  const byName = (a: { name: string }, b: { name: string }) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
  return {
    published: pages.filter((p) => p.status === 'published').sort(byName),
    drafts: pages.filter((p) => p.status !== 'published').sort(byName),
  };
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message) || fallback;

export default function PageDomains() {
  return (
    <FeedbackProvider>
      <DomainsScreen />
    </FeedbackProvider>
  );
}

function DomainsScreen() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'superadmin';
  const toast = useToast();
  const confirm = useConfirm();

  const [data, setData] = useState<DomainsResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [pages, setPages] = useState<LandingPage[] | null>(null);
  const [pagesError, setPagesError] = useState('');
  const [checking, setChecking] = useState<number[]>([]);
  const [savingRoot, setSavingRoot] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);
  const [input, setInput] = useState('');
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState('');
  const alive = useRef(true);
  const autoChecked = useRef(false);
  const loadRequest = useRef(0);
  const revision = useRef(0);
  const domainActions = useRef(new Set<number>());
  const addPending = useRef(false);
  // Set again on mount: StrictMode mounts twice in development.
  useEffect(() => { alive.current = true; return () => { alive.current = false; loadRequest.current++; }; }, []);

  const loadPages = useCallback(async () => {
    setPagesError('');
    try {
      const list = await listPages();
      if (alive.current) setPages(list);
    } catch {
      if (alive.current) setPagesError('Pages couldn’t load. Try again to choose a homepage.');
    }
  }, []);

  const load = useCallback(async () => {
    const request = ++loadRequest.current;
    const startedAt = revision.current;
    setError('');
    try {
      const next = await listDomains();
      if (alive.current && request === loadRequest.current && startedAt === revision.current) setData(next);
    } catch (e) {
      if (alive.current && request === loadRequest.current) setError(errorText(e, 'The domains did not load.'));
    } finally {
      if (alive.current && request === loadRequest.current) { setLoading(false); setRefreshing(false); }
    }
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (isAdmin) void loadPages(); }, [isAdmin, loadPages]);

  const replace = (d: PageDomain) => setData((cur) => cur && { ...cur, domains: cur.domains.map((x) => (x.id === d.id ? d : x)) });

  const check = useCallback(async (d: PageDomain, quiet = false) => {
    if (domainActions.current.has(d.id)) return;
    domainActions.current.add(d.id); revision.current++;
    setChecking((ids) => [...ids, d.id]);
    try {
      const next = await checkDomain(d.id);
      if (!alive.current) return;
      setData((cur) => cur && { ...cur, domains: cur.domains.map((x) => (x.id === next.id ? next : x)) });
      if (quiet) return;
      if (next.status === 'active') toast(`${next.domain} is live`);
      else if (next.status === 'error') toast(next.last_error || `${next.domain} has a problem. See the note under its status.`, 'error');
      else toast(next.last_error || 'Not connected yet. DNS changes can take a few minutes.', 'info');
    } catch (e) {
      if (!quiet && alive.current) toast(errorText(e, 'The check did not finish. Try again.'), 'error');
    } finally {
      domainActions.current.delete(d.id);
      if (alive.current) setChecking((ids) => ids.filter((id) => id !== d.id));
    }
  }, [toast]);

  // Opening the tab checks every domain that is not live yet, once, one at a time.
  useEffect(() => {
    if (!data || autoChecked.current || !isAdmin || !data.configured) return;
    autoChecked.current = true;
    const waiting = data.domains.filter((d) => d.status !== 'active');
    void (async () => { for (const d of waiting) { if (!alive.current) return; await check(d, true); } })();
  }, [data, isAdmin, check]);

  async function add(e: FormEvent) {
    e.preventDefault();
    if (addPending.current) return;
    const domain = normalizeDomain(input);
    if (!isValidDomain(domain)) { setAddError('Enter a domain like go.yourdomain.com.'); return; }
    addPending.current = true; revision.current++;
    setAdding(true); setAddError('');
    try {
      const added = await addDomain(domain);
      if (!alive.current) return;
      setData((cur) => cur && { ...cur, domains: [added, ...cur.domains.filter((x) => x.id !== added.id)] });
      setInput('');
      toast(added.status === 'active' ? `${added.domain} added and live` : `${added.domain} added. Add its DNS records next.`);
    } catch (err) {
      if (alive.current) setAddError(errorText(err, 'The domain could not be added. Try again.'));
    } finally {
      addPending.current = false;
      if (alive.current) setAdding(false);
    }
  }

  async function saveRoot(d: PageDomain, pageId: string) {
    if (domainActions.current.has(d.id) || savingRoot !== null) return;
    domainActions.current.add(d.id); revision.current++;
    setSavingRoot(d.id);
    try {
      const next = await setDomainRoot(d.id, pageId || null);
      if (!alive.current) return;
      replace(next);
      toast(pageId ? `Homepage of ${d.domain} saved` : `${d.domain} has no homepage now`);
    } catch (e) {
      if (alive.current) toast(errorText(e, 'The homepage did not save. Try again.'), 'error');
    } finally {
      domainActions.current.delete(d.id);
      if (alive.current) setSavingRoot(null);
    }
  }

  async function remove(d: PageDomain) {
    if (domainActions.current.has(d.id)) return;
    domainActions.current.add(d.id);
    const ok = await confirm({
      title: `Remove ${d.domain}?`,
      body: 'Your pages stop loading on this domain. Its DNS records stay at your DNS provider until you delete them there.',
      confirmLabel: 'Remove domain',
      danger: true,
    });
    if (!ok || !alive.current) { domainActions.current.delete(d.id); return; }
    revision.current++;
    setRemoving(d.id);
    try {
      await removeDomain(d.id);
      if (!alive.current) return;
      setData((cur) => cur && { ...cur, domains: cur.domains.filter((x) => x.id !== d.id) });
      toast(`${d.domain} removed`);
    } catch (e) {
      if (alive.current) toast(errorText(e, 'The domain was not removed. Try again.'), 'error');
    } finally {
      domainActions.current.delete(d.id);
      if (alive.current) setRemoving(null);
    }
  }

  async function copy(value: string, what: string) {
    if (await copyText(value)) toast(`${what} copied`);
    else toast('Couldn’t copy. Select it and copy it yourself.', 'error');
  }

  const options = useMemo(() => homepageOptions(pages ?? []), [pages]);
  const domains = data?.domains ?? [];
  const example = domains.find((d) => d.status === 'active')?.domain ?? domains[0]?.domain ?? 'go.yourdomain.com';
  const columns = isAdmin ? 5 : 4;
  // Refresh is the header button every list page has, not an icon in the table's footer.
  const refreshButton = (
    <button type="button" className="button secondary" disabled={!data || refreshing || adding || savingRoot !== null || removing !== null || checking.length > 0}
      onClick={() => { setRefreshing(true); void load(); }}>
      <RefreshCw className={refreshing ? 'spin' : ''} aria-hidden="true" />{refreshing ? 'Refreshing…' : 'Refresh'}
    </button>
  );

  return (
    <div className="page-domains">
      <PageHeader title="Domains" actions={refreshButton} />
      {loading && !data ? <PageSkeleton cards={0} />
        : !data ? <ErrorState message={error || 'The domains did not load.'} onRetry={() => { setLoading(true); void load(); }} />
        : (
          <>
            {error && <p className="inline-notice" role="alert">Domains couldn’t refresh. {error} <button type="button" className="button secondary small" onClick={() => { void load(); }}>Retry domains</button></p>}
            {pagesError && <p className="inline-notice" role="alert">{pagesError} <button type="button" className="button secondary small" onClick={() => { void loadPages(); }}>Retry pages</button></p>}
            <p className="page-domains-intro">
              Every published page works at <code>https://{example}/page-link</code>, and the homepage shows at <code>https://{example}/</code>.
            </p>
            {!data.configured && <p className="inline-notice page-domains-notice" role="status">Custom domains are not set up on this server yet.</p>}

            {isAdmin && data.configured && (
              <form className="panel page-domains-add" onSubmit={(e) => { void add(e); }} noValidate>
                <label htmlFor="page-domain-input">Add a domain</label>
                <div className="page-domains-add-row">
                  <input id="page-domain-input" type="text" inputMode="url" autoComplete="off" autoCapitalize="none" autoCorrect="off" spellCheck={false}
                    placeholder="go.yourdomain.com" value={input} aria-invalid={addError ? true : undefined}
                    aria-describedby={addError ? 'page-domain-error' : undefined}
                    onChange={(e) => { setInput(e.target.value); if (addError) setAddError(''); }} />
                  <button type="submit" className="button" disabled={adding || !input.trim()}>{adding ? 'Adding…' : 'Add'}</button>
                </div>
                {addError && <p id="page-domain-error" className="crm-error" role="alert">{addError}</p>}
                {!addError && isValidDomain(normalizeDomain(input)) && isApex(normalizeDomain(input)) && (
                  <p className="page-domains-apex" role="note">{apexWarning(normalizeDomain(input))}</p>
                )}
              </form>
            )}

            {!domains.length ? (
              <section className="panel">
                <EmptyState icon={<Globe aria-hidden="true" />} title="No domains yet"
                  description={isAdmin
                    ? (data.configured ? 'Add one above to put your pages on your own address.' : 'Domains can be added once the server is set up for them.')
                    : 'An administrator can connect one.'} />
              </section>
            ) : (
              <section className="panel table-wrap page-domains-panel">
                <table className="page-domains-table">
                  <thead>
                    <tr>
                      <th>Domain</th>
                      <th>Status</th>
                      <th>Homepage</th>
                      <th>Last checked</th>
                      {isAdmin && <th><span className="sr-only">Actions</span></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {domains.map((d) => {
                      const status = STATUS[d.status] ?? STATUS.pending;
                      const busy = checking.includes(d.id);
                      const showDns = d.status !== 'active' && d.records.length > 0;
                      const root = d.root_page;
                      // The current homepage stays selectable even when the page list did not load.
                      const missingRoot = root && !(pages ?? []).some((p) => p.id === root.id);
                      return (
                        <Fragment key={d.id}>
                          <tr className={showDns ? 'has-dns' : undefined} data-domain={d.domain}>
                            <td data-label="Domain" className="page-domain-cell">
                              {d.status === 'active' ? (
                                <a className="page-domain-name" href={`https://${d.domain}/`} target="_blank" rel="noreferrer" title={`Open https://${d.domain}/`}>
                                  {d.domain}<ExternalLink aria-hidden="true" />
                                </a>
                              ) : <span className="page-domain-name">{d.domain}</span>}
                            </td>
                            <td data-label="Status">
                              <span className="page-domain-status">
                                <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                                {d.status !== 'active' && d.last_error && <small className={d.status === 'error' ? 'page-domain-error' : 'page-domain-error page-domain-waiting'}>{d.last_error}</small>}
                              </span>
                            </td>
                            <td data-label="Homepage">
                              {isAdmin ? (
                                <span className="page-domain-root">
                                  <select aria-label={`Homepage of ${d.domain}`} value={d.root_page_id ?? ''} disabled={savingRoot !== null || busy || removing === d.id || !pages}
                                    onChange={(e) => { void saveRoot(d, e.target.value); }}>
                                    <option value="">None</option>
                                    {missingRoot && root && <option value={root.id}>{root.name}</option>}
                                    {options.published.length > 0 && (
                                      <optgroup label="Published">
                                        {options.published.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                      </optgroup>
                                    )}
                                    {options.drafts.length > 0 && (
                                      <optgroup label="Drafts">
                                        {options.drafts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                                      </optgroup>
                                    )}
                                  </select>
                                  {root && root.status !== 'published' && <small className="page-domain-note">Draft. Publish it so visitors see it.</small>}
                                </span>
                              ) : (
                                <span className="page-domain-root-text">
                                  {root ? root.name : <span className="page-domain-muted">None</span>}
                                  {root && root.status !== 'published' && <small className="page-domain-note">Draft, not shown yet</small>}
                                </span>
                              )}
                            </td>
                            <td data-label="Last checked" className="page-domain-checked">
                              {d.last_checked_at ? formatDateTime(d.last_checked_at) : <span className="page-domain-muted">Not yet</span>}
                            </td>
                            {isAdmin && (
                              <td className="page-domain-actions">
                                <span>
                                  <button type="button" className="button secondary small" disabled={busy || savingRoot === d.id || removing === d.id || !data.configured} onClick={() => { void check(d); }}>
                                    <RefreshCw className={busy ? 'spin' : undefined} aria-hidden="true" />{busy ? 'Checking…' : 'Check'}
                                  </button>
                                  <button type="button" className="button secondary small page-domain-remove" disabled={busy || savingRoot === d.id || removing !== null} onClick={() => { void remove(d); }}>
                                    {removing === d.id ? 'Removing…' : 'Remove'}
                                  </button>
                                </span>
                              </td>
                            )}
                          </tr>
                          {showDns && (
                            <tr className="page-domain-dns-row">
                              <td colSpan={columns}>
                                <DnsRecords domain={d.domain} apex={d.apex} records={d.records} canCheck={isAdmin && data.configured} onCopy={copy} />
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
                <div className="crm-table-foot">
                  <span>{domains.length} {domains.length === 1 ? 'domain' : 'domains'}</span>
                </div>
              </section>
            )}
          </>
        )}
    </div>
  );
}

/** Connecting a main address (example.com, not go.example.com) points the whole
 *  domain here: whatever website runs on it now stops showing. */
function apexWarning(domain: string): string {
  return `This is your main address. Its records replace the website there now. Use a subdomain like go.${domain} unless nothing else runs on it.`;
}

/** The records to add at the DNS provider, each name and value with a Copy button. */
function DnsRecords({ domain, apex, records, canCheck, onCopy }: {
  domain: string;
  apex?: string;
  records: DnsRecord[];
  canCheck: boolean;
  onCopy: (value: string, what: string) => void;
}) {
  return (
    <div className="page-domain-dns">
      <p>{canCheck
        ? 'Add these records at your DNS provider, then press Check. It can take a few minutes.'
        : 'Add these records at your DNS provider. It can take a few minutes.'}</p>
      <p className="page-domain-dns-tips">@ means the domain itself. If a record with the same type and name is already there, change it instead of adding a second one. On Cloudflare, set the proxy to DNS only.</p>
      {isApex(domain, apex) && <p className="page-domains-apex" role="note">{apexWarning(domain)}</p>}
      <table className="page-domain-records" aria-label={`DNS records for ${domain}`}>
        <thead><tr><th>Type</th><th>Name</th><th>Value</th></tr></thead>
        <tbody>
          {records.map((r, i) => (
            <tr key={`${r.type}:${r.name}:${i}`}>
              <td data-label="Type" className="page-domain-type">{r.type}</td>
              <td data-label="Name">
                <span className="page-domain-copy">
                  <code>{r.name}</code>
                  <button type="button" className="icon-button" aria-label={`Copy name ${r.name}`} title="Copy" onClick={() => onCopy(r.name, 'Name')}>
                    <Copy aria-hidden="true" />
                  </button>
                </span>
              </td>
              <td data-label="Value">
                <span className="page-domain-copy">
                  <code>{r.value}</code>
                  <button type="button" className="icon-button" aria-label={`Copy value for ${r.type} ${r.name}`} title="Copy" onClick={() => onCopy(r.value, 'Value')}>
                    <Copy aria-hidden="true" />
                  </button>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
